import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Tooltip } from '../../components';
import Icons from '../Icons/Icons';
import css from './ItemListCard.module.css';
import noImage from '../../assets/no-image.jpg';
import {
  trackArticleEvent,
  trackModuleClicked,
  trackSymptomClicked,
} from '../../util/mixpanelHelpers';
import { itemListCardPageName, MIXPANEL_EVENTS } from '../../util/enums';
import { variable_article, variable_video } from '../../util/constants';

const ItemListCard = ({
  item,
  onClick,
  onFavoriteClick,
  showFavorite = false,
  isFavorited = false,
  showProgress = false,
  progressPercent = 0,
  icon,
  timeInfo,
  className,
  intl,
  pageName = '',
}) => {
  const handleFavoriteClick = e => {
    e.stopPropagation();
    if (onFavoriteClick) {
      onFavoriteClick(item);
    }
  };

  const handleCardClick = () => {
    switch (pageName) {
      case itemListCardPageName.symptom:
        trackSymptomClicked(item?.id);
        break;
      case itemListCardPageName.favorites:
        if (item?.type === variable_article) {
          trackArticleEvent(MIXPANEL_EVENTS.ARTICLE_CLICKED, {
            articleId: item?.id,
            articleTitle: item?.title,
          });
        }
        break;
      default:
        if (item?.type === variable_video) {
          trackModuleClicked(item?.id, item?.title);
        }
        break;
    }

    if (onClick) {
      onClick(item);
    }
  };

  return (
    <div
      className={classNames(css.categoryBlock, className)}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyPress={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleCardClick();
        }
      }}
    >
      <div className={css.categoryImg}>
        <img
          src={item.thumbnail || item.image || noImage}
          alt={item.title}
          loading="lazy"
          decoding="async"
        />
      </div>

      {showFavorite && (
        <span
          role="button"
          aria-label={
            intl
              ? intl.formatMessage({
                  id: isFavorited ? 'GuidesPage.removeFromFavorites' : 'GuidesPage.addToFavorites',
                })
              : isFavorited
              ? 'GuidesPage.removeFromFavorites'
              : 'GuidesPage.addToFavorites'
          }
          tabIndex={0}
          onClick={handleFavoriteClick}
          onKeyPress={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleFavoriteClick(e);
            }
          }}
          className={css.favoriteIconNew}
        >
          {intl ? (
            <Tooltip
              className={css.favoriteToolTip}
              text={intl.formatMessage({
                id: isFavorited ? 'GuidesPage.removeFromFavorites' : 'GuidesPage.addToFavorites',
              })}
            >
              <Icons name={isFavorited ? 'heartFilled' : 'heartOutlined'} />
            </Tooltip>
          ) : (
            <Icons name={isFavorited ? 'heartFilled' : 'heartOutlined'} />
          )}
        </span>
      )}

      <div className={css.categoryInfo}>
        <span className={css.categoryTitle} title={item.title}>
          {item.title}
        </span>

        <div className={css.progressBarSection}>
          <div className={css.progressInfo}>
            {icon && <span>{icon}</span>}
            {timeInfo && <span className={css.progressTimeLeft}>{timeInfo}</span>}
          </div>

          {showProgress && progressPercent > 0 && (
            <div className={css.videoProgressBar}>
              <span
                className={css.videoProgress}
                style={{ width: `${progressPercent}%` }}
                aria-label={`${Math.round(progressPercent)}% watched`}
              ></span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

ItemListCard.propTypes = {
  item: PropTypes.shape({
    title: PropTypes.string.isRequired,
    thumbnail: PropTypes.string,
    image: PropTypes.string,
  }).isRequired,
  onClick: PropTypes.func,
  onFavoriteClick: PropTypes.func,
  showFavorite: PropTypes.bool,
  isFavorited: PropTypes.bool,
  showProgress: PropTypes.bool,
  progressPercent: PropTypes.number,
  icon: PropTypes.node,
  timeInfo: PropTypes.string,
  className: PropTypes.string,
  intl: PropTypes.object,
};

export default ItemListCard;
